import { useLocation, useNavigate } from "@remix-run/react";
import { ShieldCheck } from "lucide-react";

import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { useBaaNudgeOpen } from "~/features/baa-nudge/context";
import { useLoginDismissal } from "~/features/baa-nudge/hooks/use-login-dismissal";
import { useUserDetails } from "~/hooks/queries/use-user-details";

// AIDEV-NOTE: Sits in the layout header slot beside BillingGlobalBanner. It never
// opens anything by itself: the CTA opens the eligibility dialog, "Not now"
// hides the banner for this login (sessionStorage key shared with the dialog).
export function BaaNudgeBanner() {
    const { data: userDetails } = useUserDetails();
    const location = useLocation();
    const navigate = useNavigate();
    const { setOpen } = useBaaNudgeOpen();
    const { dismissed, dismiss } = useLoginDismissal();

    const state = userDetails?.practitionerBaaNudge ?? { kind: "hidden" };
    const excludedPath =
        location.pathname.startsWith("/onboarding") ||
        location.pathname === "/practitioner/baa" ||
        location.pathname === "/practitioner/dpa";

    if (dismissed || excludedPath || state.kind === "hidden") return null;

    const isReminder = state.kind === "signature-reminder";
    const primary = isReminder
        ? "Regulatory: your practice needs a BAA, and it's ready to sign."
        : "Regulatory: a quick check on where you practice.";
    const secondary = isReminder
        ? "Signing takes about two minutes."
        : "Two questions decide which agreement supports your practice.";
    const cta = isReminder ? "Review and sign the BAA" : "Answer now";

    const onCta = () => {
        if (isReminder) {
            const redirect = `${location.pathname}${location.search}${location.hash}`;
            navigate(
                `/onboarding/practitioner/baa?redirect=${encodeURIComponent(
                    redirect,
                )}`,
            );
            return;
        }
        setOpen(true);
    };

    return (
        <Alert
            data-testid="baa-nudge-banner"
            className="flex items-center rounded-none border-x-0 border-t-0 border-brand-forest-200 bg-brand-forest-50 text-brand-forest-900"
        >
            <AlertDescription className="flex w-full items-center justify-between gap-4">
                <span className="flex items-start gap-2.5">
                    <ShieldCheck className="mt-0.5 size-4 shrink-0 text-brand-forest-600" />
                    <span className="flex flex-col gap-0.5">
                        <span className="text-sm font-medium">{primary}</span>
                        <span className="text-sm opacity-80">{secondary}</span>
                    </span>
                </span>
                <span className="flex shrink-0 items-center gap-2">
                    <Button
                        size="sm"
                        analyticsId="baa-nudge-banner-cta"
                        onClick={onCta}
                    >
                        {cta}
                    </Button>
                    <Button
                        size="sm"
                        variant="ghost"
                        analyticsId="baa-nudge-banner-dismiss"
                        onClick={dismiss}
                    >
                        Not now
                    </Button>
                </span>
            </AlertDescription>
        </Alert>
    );
}
